import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { object } from 'prop-types'
import { updateSterilizationCalls } from '@store/slice/sterilizationSlice'
import { updateNotification } from '@store/slice/mainSlice'
import { get } from '@util/xhrAPI'
import { useTheme } from '@table-library/react-table-library/theme'
import { getTheme } from '@table-library/react-table-library/baseline'
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from "@table-library/react-table-library/table"
import usePathGenerator from '@hook/usePathGenerator'
import useDeleteButton from '@hook/useDeleteButton'
import Buttons from '@ui/elements/Buttons'
import Div from '@ui/layout/Div'

const SterilizationsListTable = ({ dataSterilizationCalls }) => {
  const [t] = useTranslation('common')
  const { calls } = useSelector(state => state.sterilization)
  const { mission } = useSelector(state => state.main)
  const { isCompany, isCoOwnership } = mission
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const deleteButton = useDeleteButton()
  const theme = useTheme([getTheme(), {
    Table: `
         --data-table-library_grid-template-columns: 5% 15% 22% 30% repeat(${isCompany || isCoOwnership ? 3 : 2}, minmax(0, 1fr));
      `,
  },])
  const resize = { resizerHighlight: "#dde2eb", resizerWidth: 5 }

  const getDynamicPath = usePathGenerator()

  const goToShowPage = id => navigate(getDynamicPath('sterilizationShow', { id }))

  const renderMissionSubjectsStat = (id, missionSubjectsStat) => {
    if (mission.missionSubjects.length > 1) {
      return mission.missionSubjects.map(missionSubject => {
        return (
          <h5 key={`${id}-${missionSubject.label}-stat`}>
            <span>{missionSubject.label}</span>: {missionSubjectsStat[missionSubject.label] || 0}
          </h5>
        )
      })
    }
  }

  const removeCall = id => {
    const sterilizationCall = calls[id]
    get(`${import.meta.env.VITE_API_URL}${sterilizationCall.links.remove}`, response => {
      const { code } = response
      if (code == 200) {
        const callsList = { ...calls }
        delete callsList[id]
        dispatch(updateSterilizationCalls({ ...callsList }))
        dispatch(updateNotification({ type: 'is-success', message: t('messages.removed_successfuly', { title: sterilizationCall.name }) }))
      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t('messages.error_occured') }))
      }
    })
  }

  return (
    <>
      <Table data={dataSterilizationCalls} theme={theme} layout={{ custom: true }} className='sterilizations'>
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell resize={resize}>ID</HeaderCell>
                <HeaderCell resize={resize}>{t('table.FullName')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Address')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Treatment')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Date')}</HeaderCell>
                {isCompany || isCoOwnership ? <HeaderCell resize={resize}>{t('table.CallerType')}</HeaderCell> : <></>}
                <HeaderCell resize={resize}>Actions</HeaderCell>
              </HeaderRow>
            </Header>
            <Body>
              {tableList.map(item =>
                <Row key={item.id} item={item} className={item.isSecondScanned ? 'is-second-scanned' : (item.isFirstScanned ? 'is-first-scanned' : 'non-scanned')}>
                  <Cell>#{item.id}</Cell>
                  <Cell>{item.missionResponsibleFullName}</Cell>
                  <Cell>{item.companyName ? `${item.companyName},` : ''}{item.sterilizationDistrict}, {item.sterilizationAddress},{item.sterilizationZipCode}</Cell>
                  <Cell className='scan-cell'>
                    <h4><span>{t('table.signaled_nests')}</span> :{item.nestsNumber}</h4>
                    <h3>{t('table.scan')}</h3>
                    <Div cssClass='columns'>
                      {item.scansData.isFirstScanned
                        ? <Div cssClass='column'>
                          <h4>{t('table.1st_scan')}</h4>
                          <h5><span>{t('table.found_nests')}</span> :{item.scansData.firstScan.totalNests}</h5>
                          <h5><span>{t('table.empty_nests')}</span> :{item.scansData.firstScan.totalEmptyNests}</h5>
                          <h5><span>{t('table.nb_eggs')}</span> :{item.scansData.firstScan.totalEggs}</h5>
                          <h5><span>{t('table.nb_non_treated_eggs')}</span> :{item.scansData.firstScan.totalNonTreatedEggs}</h5>
                          <h5><span>{t('table.nb_birds')}</span> :{item.scansData.firstScan.totalBirds}</h5>
                          {renderMissionSubjectsStat(item.id, item.scansData.firstScan.missionSubjects)}
                        </Div>
                        : t('table.pending')
                      }
                      {item.scansData.isSecondScanned
                        ? <Div cssClass='column'>
                          <h4>{t('table.2nd_scan')}</h4>
                          <h5><span>{t('table.found_nests')}</span> :{item.scansData.secondScan.totalNests}</h5>
                          <h5><span>{t('table.empty_nests')}</span> :{item.scansData.secondScan.totalEmptyNests}</h5>
                          <h5><span>{t('table.nb_eggs')}</span> :{item.scansData.secondScan.totalEggs}</h5>
                          <h5><span>{t('table.nb_non_treated_eggs')}</span> :{item.scansData.secondScan.totalNonTreatedEggs}</h5>
                          <h5><span>{t('table.nb_birds')}</span> :{item.scansData.secondScan.totalBirds}</h5>
                          {renderMissionSubjectsStat(item.id, item.scansData.secondScan.missionSubjects)}
                        </Div>
                        : t('table.pending')
                      }
                    </Div>
                  </Cell>
                  <Cell>{item.createdAt}</Cell>
                  {
                    isCompany || isCoOwnership
                      ? <Cell className={!isCompany ? 'is-hidden' : ''}>{t(`table.${item.callType}`)}</Cell>
                      : <></>
                  }
                  <Cell className='table-actions'>
                    <Buttons buttons={[
                      {
                        icon: 'icon-clipboard',
                        cssClass: 'is-info is-small',
                        callback: () => goToShowPage(item.id)
                      }
                    ]} />
                    {deleteButton(item.id, item.sterilizationAddress, removeCall)}
                  </Cell>
                </Row>
              )}
            </Body>
          </>
        )}
      </Table>
    </>
  )
}

SterilizationsListTable.propTypes = {
  dataSterilizationCalls: object
}

export default SterilizationsListTable